import * as React from "react";
import { CardContent } from "../../../components/antd/card/CardContent";
import { CodeEditor } from "../../group/identityMatcher/codeEditor";
import { formSet, formSetValue, useCpFormValue } from "../../gvc/testNewForm/utils";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { store } from "../../../store";
import { ImportButtonRaw, DropZone } from "../../../components/import";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { SecretCodeEditorObfuscated } from "./SecretCodeEditorObfuscated";

interface Props {}
export const SecretDataTLS: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const tlsKey = useCpFormValue(`tls.key`);
  const tlsCert = useCpFormValue(`tls.cert`);
  const tlsChain = useCpFormValue(`tls.chain`);

  return (
    <>
      <CardContent>
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-2">
            <div>TLS Cert</div>
            <ImportButtonRaw
              formId={id}
              fieldName="tls.cert"
              actionLabel="Import TLS certificate"
              accept=".pem,.crt,.cer,.txt"
              existingHasContent={!!tlsCert?.trim()}
              explanation={
                <div className="text-xs">
                  <div className="font-semibold mb-1">Accepted formats:</div>
                  <div>
                    <strong>.pem, .crt, .cer</strong> — TLS certificate
                  </div>
                  <div className="mt-1 opacity-75">PEM-encoded X.509 certificate</div>
                </div>
              }
            />
          </div>
          <SecretCodeEditorObfuscated visibilityName="meta.codeEditorVisibility.tls.cert" contentName="tls.cert">
            <DropZone
              formId={id}
              fieldName="tls.cert"
              actionLabel="Import TLS certificate"
              accept={[".pem", ".crt", ".cer", ".txt"]}
            >
              <CodeEditor
                value={tlsCert}
                setValue={(value) => formSetValue(id, `tls.cert`, value)}
                onChange={() => {
                  const name = "tls.cert";
                  formSet(id, `fieldStates.${name}.modified`, true);

                  const form = store.getState().form.instances[id];
                  const onChangeCounter = form.meta.onChangeCounter;
                  formSet(id, `meta.onChangeCounter`, onChangeCounter + 1);
                }}
                onFocus={() => {
                  const name = "tls.cert";
                  formSet(id, `fieldStates.${name}.focused`, true);
                  formSet(id, `fieldStates.${name}.visited`, true);
                }}
                onBlur={() => {
                  const name = "tls.cert";
                  formSet(id, `fieldStates.${name}.focused`, false);
                  formSet(id, `fieldStates.${name}.touched`, true);

                  const form = store.getState().form.instances[id];
                  const onBlurCounter = form.meta.onBlurCounter;
                  formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
                }}
                language={"javascript"}
                options={{
                  basicSetup: {
                    lineNumbers: true,
                  },
                  style: {
                    maxHeight: 500,
                    overflow: "auto",
                    fontSize: 14,
                  },
                }}
              />
            </DropZone>
          </SecretCodeEditorObfuscated>
          <CpErrorWarning name={`tls.cert`} />
        </div>
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-2">
            <div>TLS Key</div>
            <ImportButtonRaw
              formId={id}
              fieldName="tls.key"
              actionLabel="Import TLS key"
              accept=".pem,.key,.txt"
              existingHasContent={!!tlsKey?.trim()}
              explanation={
                <div className="text-xs">
                  <div className="font-semibold mb-1">Accepted formats:</div>
                  <div>
                    <strong>.pem, .key</strong> — TLS private key
                  </div>
                  <div className="mt-1 opacity-75">PEM-encoded RSA/EC private key (unencrypted)</div>
                </div>
              }
            />
          </div>
          <SecretCodeEditorObfuscated visibilityName="meta.codeEditorVisibility.tls.key" contentName="tls.key">
            <DropZone formId={id} fieldName="tls.key" actionLabel="Import TLS key" accept={[".pem", ".key", ".txt"]}>
              <CodeEditor
                value={tlsKey}
                setValue={(value) => {
                  formSetValue(id, `tls.key`, value);
                }}
                onChange={() => {
                  const name = "tls.key";
                  formSet(id, `fieldStates.${name}.modified`, true);

                  const form = store.getState().form.instances[id];
                  const onChangeCounter = form.meta.onChangeCounter;
                  formSet(id, `meta.onChangeCounter`, onChangeCounter + 1);
                }}
                onFocus={() => {
                  const name = "tls.key";
                  formSet(id, `fieldStates.${name}.focused`, true);
                  formSet(id, `fieldStates.${name}.visited`, true);
                }}
                onBlur={() => {
                  const name = "tls.key";
                  formSet(id, `fieldStates.${name}.focused`, false);
                  formSet(id, `fieldStates.${name}.touched`, true);

                  const form = store.getState().form.instances[id];
                  const onBlurCounter = form.meta.onBlurCounter;
                  formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
                }}
                language={"javascript"}
                options={{
                  basicSetup: {
                    lineNumbers: true,
                  },
                  style: {
                    maxHeight: 500,
                    overflow: "auto",
                    fontSize: 14,
                  },
                }}
              />
            </DropZone>
          </SecretCodeEditorObfuscated>
        </div>
        <div>
          <div className="flex items-center gap-4 mb-2">
            <div>TLS Chain</div>
            <ImportButtonRaw
              formId={id}
              fieldName="tls.chain"
              actionLabel="Import TLS chain"
              accept=".pem,.crt,.cer,.txt"
              existingHasContent={!!tlsChain?.trim()}
              explanation={
                <div className="text-xs">
                  <div className="font-semibold mb-1">Accepted formats:</div>
                  <div>
                    <strong>.pem, .crt</strong> — Certificate chain
                  </div>
                  <div className="mt-1 opacity-75">PEM-encoded intermediate certificates (optional)</div>
                </div>
              }
            />
          </div>
          <SecretCodeEditorObfuscated visibilityName="meta.codeEditorVisibility.tls.chain" contentName="tls.chain">
            <DropZone
              formId={id}
              fieldName="tls.chain"
              actionLabel="Import TLS chain"
              accept={[".pem", ".crt", ".cer", ".txt"]}
            >
              <CodeEditor
                value={tlsChain}
                setValue={(value) => formSetValue(id, `tls.chain`, value)}
                onChange={() => {
                  const name = "tls.chain";
                  formSet(id, `fieldStates.${name}.modified`, true);

                  const form = store.getState().form.instances[id];
                  const onChangeCounter = form.meta.onChangeCounter;
                  formSet(id, `meta.onChangeCounter`, onChangeCounter + 1);
                }}
                onFocus={() => {
                  const name = "tls.chain";
                  formSet(id, `fieldStates.${name}.focused`, true);
                  formSet(id, `fieldStates.${name}.visited`, true);
                }}
                onBlur={() => {
                  const name = "tls.chain";
                  formSet(id, `fieldStates.${name}.focused`, false);
                  formSet(id, `fieldStates.${name}.touched`, true);

                  const form = store.getState().form.instances[id];
                  const onBlurCounter = form.meta.onBlurCounter;
                  formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
                }}
                language={"javascript"}
                options={{
                  basicSetup: {
                    lineNumbers: true,
                  },
                  style: {
                    maxHeight: 500,
                    overflow: "auto",
                    fontSize: 14,
                  },
                }}
              />
            </DropZone>
          </SecretCodeEditorObfuscated>
        </div>
      </CardContent>
    </>
  );
};
